import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import styled from '@emotion/styled';
import { assessmentPatchSession } from 'actions/assessment';

const BODY_FONT = 'Rubik-Regular, system-ui, sans-serif';
const CONSENT_VERSION = '2026-04-26';
const POINT_KEYS = ['consentPoint1', 'consentPoint2', 'consentPoint3', 'consentPoint4'];

const Heading = styled(Typography)({
  fontSize: 24,
  fontWeight: 700,
  textAlign: 'left',
  marginBottom: 14,
  lineHeight: 1.3,
});

const Lede = styled(Typography)(({ theme }) => ({
  fontFamily: BODY_FONT,
  fontSize: 14,
  color: theme.palette.grey[700],
  textAlign: 'left',
  marginBottom: 24,
  lineHeight: 1.6,
}));

const PointsList = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  marginBottom: 28,
});

const PointRow = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 4,
  padding: '6px 0',
  cursor: 'pointer',
});

const PointText = styled('span')(({ theme }) => ({
  flex: 1,
  fontFamily: BODY_FONT,
  fontSize: 14,
  lineHeight: 1.55,
  color: theme.palette.grey[800],
  paddingTop: 9,
}));

const PointCheckbox = styled(Checkbox)({
  padding: 6,
  marginTop: 1,
});

const PrivacyLinkInline = styled('a')(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'underline',
  '&:hover': { color: theme.palette.primary.dark },
}));

const PrimaryButton = styled(Button)({
  fontFamily: BODY_FONT,
  borderRadius: 25,
  padding: '14px 32px',
  fontSize: 16,
  fontWeight: 600,
  textTransform: 'none',
  width: 240,
  alignSelf: 'flex-start',
});

export default function Consent({ onAccept }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [checked, setChecked] = useState(() => POINT_KEYS.map(() => false));
  const [submitting, setSubmitting] = useState(false);

  const allChecked = checked.every(Boolean);

  const toggle = (idx) =>
    setChecked((prev) => prev.map((v, i) => (i === idx ? !v : v)));

  const stopBubble = (e) => e.stopPropagation();

  const handleContinue = async () => {
    if (!allChecked || submitting) return;
    setSubmitting(true);
    const consent = { accepted_at: new Date().toISOString(), version: CONSENT_VERSION };
    try {
      await dispatch(assessmentPatchSession({ consent }));
    } catch (err) {
      console.error('Consent persist failed:', err);
      // Don't block the user — local copy is enough to advance.
    }
    onAccept?.(consent);
  };

  const renderPointContent = (key) => {
    if (key === 'consentPoint3') {
      return (
        <Trans
          i18nKey={`Assessment.${key}`}
          components={{
            a: (
              <PrivacyLinkInline
                href="/policy/privacy"
                target="_blank"
                rel="noopener noreferrer"
                onClick={stopBubble}
              />
            ),
          }}
        />
      );
    }
    return t(`Assessment.${key}`);
  };

  return (
    <>
      <Heading variant="h1">{t('Assessment.consentHeading')}</Heading>
      <Lede>{t('Assessment.consentBody')}</Lede>

      <PointsList>
        {POINT_KEYS.map((key, idx) => (
          <PointRow key={key} onClick={() => toggle(idx)}>
            <PointCheckbox
              checked={checked[idx]}
              onClick={stopBubble}
              onChange={() => toggle(idx)}
              color="primary"
              slotProps={{ input: { 'aria-label': t(`Assessment.${key}`) } }}
            />
            <PointText>{renderPointContent(key)}</PointText>
          </PointRow>
        ))}
      </PointsList>

      <PrimaryButton
        variant="contained"
        color="primary"
        size="large"
        disabled={!allChecked || submitting}
        onClick={handleContinue}
      >
        {submitting ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : t('Assessment.consentContinue')}
      </PrimaryButton>
    </>
  );
}
